import { useCallback, useRef, useState } from "react";
import {
  Upload,
  FileText,
  FileSpreadsheet,
  PenLine,
  Loader2,
  X,
  Plus,
  Download,
} from "lucide-react";
import "./PortfolioUploader.css";

const TABS = [
  { id: "cams", label: "CAMS Statement", icon: FileText },
  { id: "kfintech", label: "KFintech Statement", icon: FileText },
  { id: "pdf", label: "PDF", icon: FileText },
  { id: "excel", label: "Excel", icon: FileSpreadsheet },
  { id: "manual", label: "Manual Entry", icon: PenLine },
];

const emptyRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  fundName: "",
  amount: "",
  units: "",
  invested: "",
});

function downloadSampleCsv() {
  const csv = [
    "Fund Name,Current Value,Units,Invested Value",
    "HDFC Flexi Cap Fund - Direct Growth,250000,120.5,180000",
    "SBI Magnum Midcap Fund - Direct Growth,120000,85.2,95000",
    "ICICI Prudential Liquid Fund - Direct Growth,80000,450.1,78000",
    "Nippon India ETF Gold BeES,35000,55,30000",
    "Parag Parikh Flexi Cap Fund - Direct Growth,150000,90.3,110000",
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "poonji-portfolio-sample.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function PortfolioUploader({
  onAnalyze,
  analyzing,
  error,
  previewHoldings,
  onClearPreview,
}) {
  const [tab, setTab] = useState("cams");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const inputRef = useRef(null);

  const isManual = tab === "manual";

  const acceptFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const updateRow = (id, key, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [key]: value } : r))
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (id) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const canSubmit = isManual
    ? rows.some((r) => r.fundName.trim() && Number(r.amount) > 0)
    : Boolean(file) || (previewHoldings && previewHoldings.length > 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit || analyzing) return;
    onAnalyze({
      source: tab,
      file: isManual ? null : file,
      fileName: file?.name,
      holdings: isManual ? rows : previewHoldings || [],
      isManual,
    });
  };

  return (
    <section className="pu-section pm-reveal" id="upload">
      <div className="pm-container">
        <div className="pu-header">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Upload Portfolio
          </span>
          <h2 className="pm-section-title">Import your holdings</h2>
          <p className="pm-section-sub">
            Upload CAMS / KFintech PDF, Excel or CSV — or enter funds manually.
            Analysis runs on your device; files stay with you.
          </p>
        </div>

        <div className="pu-tabs" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`pu-tab ${tab === id ? "pu-tab--active" : ""}`}
              onClick={() => {
                setTab(id);
                setFile(null);
                onClearPreview?.();
              }}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        <form className="pu-panel pm-card" onSubmit={handleSubmit}>
          {!isManual ? (
            <div
              className={`pu-dropzone ${dragOver ? "pu-dropzone--over" : ""} ${
                file ? "pu-dropzone--has-file" : ""
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
              }}
            >
              <input
                ref={inputRef}
                type="file"
                className="pu-file-input"
                accept={
                  tab === "excel"
                    ? ".xlsx,.xls,.csv"
                    : tab === "pdf" || tab === "cams" || tab === "kfintech"
                      ? ".pdf,.xlsx,.xls,.csv,.txt"
                      : ".pdf,.xlsx,.xls,.csv,.txt"
                }
                onChange={(e) => acceptFile(e.target.files?.[0])}
              />
              <div className="pu-dropzone__icon">
                <Upload size={28} />
              </div>
              {file ? (
                <>
                  <p className="pu-dropzone__title">{file.name}</p>
                  <p className="pu-dropzone__hint">
                    {(file.size / 1024).toFixed(1)} KB · Click to replace
                  </p>
                </>
              ) : (
                <>
                  <p className="pu-dropzone__title">
                    Drag & drop your {TABS.find((t) => t.id === tab)?.label}
                  </p>
                  <p className="pu-dropzone__hint">
                    PDF, Excel or CSV · Max 12 MB · Processed locally
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="pu-manual">
              <div className="pu-manual__head pu-manual__head--4">
                <span>Fund name</span>
                <span>Current value (₹)</span>
                <span>Units</span>
                <span>Invested (₹)</span>
                <span />
              </div>
              {rows.map((row) => (
                <div key={row.id} className="pu-manual__row pu-manual__row--4">
                  <input
                    type="text"
                    placeholder="e.g. HDFC Flexi Cap"
                    value={row.fundName}
                    onChange={(e) => updateRow(row.id, "fundName", e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="50000"
                    value={row.amount}
                    onChange={(e) => updateRow(row.id, "amount", e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="120.5"
                    value={row.units}
                    onChange={(e) => updateRow(row.id, "units", e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="optional"
                    value={row.invested}
                    onChange={(e) => updateRow(row.id, "invested", e.target.value)}
                  />
                  <button
                    type="button"
                    className="pu-manual__remove"
                    onClick={() => removeRow(row.id)}
                    aria-label="Remove row"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button type="button" className="pu-add-row" onClick={addRow}>
                <Plus size={16} />
                Add another fund
              </button>
            </div>
          )}

          {error && <p className="pu-error">{error}</p>}

          {previewHoldings?.length > 0 && !isManual && (
            <div className="pu-preview">
              <div className="pu-preview__head">
                <strong>{previewHoldings.length} holdings detected</strong>
                <span>Review before final scores appear below</span>
              </div>
              <div className="pu-preview__table-wrap">
                <table className="pu-preview__table">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th>Value</th>
                      <th>Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewHoldings.slice(0, 8).map((h) => (
                      <tr key={h.id || h.fundName}>
                        <td>{h.fundName}</td>
                        <td>
                          ₹{Number(h.amount).toLocaleString("en-IN")}
                        </td>
                        <td>{h.units || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewHoldings.length > 8 && (
                  <p className="pu-preview__more">
                    +{previewHoldings.length - 8} more in full analysis
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="pu-actions">
            <button
              type="submit"
              className="pm-btn pm-btn-primary"
              disabled={!canSubmit || analyzing}
            >
              {analyzing ? (
                <>
                  <Loader2 size={18} className="pu-spin" />
                  Analysing portfolio…
                </>
              ) : (
                "Analyse Portfolio"
              )}
            </button>
            {file && !isManual && (
              <button
                type="button"
                className="pm-btn pm-btn-ghost"
                onClick={() => {
                  setFile(null);
                  onClearPreview?.();
                }}
              >
                Clear file
              </button>
            )}
            <button
              type="button"
              className="pm-btn pm-btn-outline"
              onClick={downloadSampleCsv}
            >
              <Download size={16} />
              Sample CSV
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
